import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogClose, DialogContent, Field, Icon, Textarea, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useErrorMessage } from "../../../lib/api-errors";
import { zodResolver } from "../../../lib/zod-resolver";
import { useApi } from "../../../session/session-provider";
import type { ChunkView } from "../shared/types";
import { useReplaceCachedChunk } from "./use-source-detail";

/** Backend `MAX_CHUNK_CONTENT` (ChunkPatch.content max length). */
export const MAX_CHUNK_CHARS = 20_000;

interface EditChunkDialogProps {
  chunk: ChunkView;
  sourceId: string;
  onOpenChange: (open: boolean) => void;
}

/** Edit chunk text → `PATCH /v1/chunks/{id} {content}` (re-embeds). Context is read-only (API has no field for it). */
export function EditChunkDialog({ chunk, sourceId, onOpenChange }: EditChunkDialogProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const replace = useReplaceCachedChunk(sourceId);
  const k = "deployKnowledge.sourceDetail.editChunk";
  const schema = z.object({
    content: z.string().trim().min(1, t(`${k}.textRequired`)).max(MAX_CHUNK_CHARS, t(`${k}.textTooLong`, { max: MAX_CHUNK_CHARS })),
  });
  const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema), defaultValues: { content: chunk.content } });
  const save = useMutation({
    mutationFn: (content: string) => unwrap(api.PATCH("/v1/chunks/{chunk_id}", { params: { path: { chunk_id: chunk.id } }, body: { content } })),
    onSuccess: (updated) => {
      replace(updated);
      toast.success(t(`${k}.saved`));
      onOpenChange(false);
    },
    onError: (error) => toast.error(message(error)),
  });
  const n = chunk.ordinal + 1;
  const path = chunk.heading_path.join(" › ");
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        title={<>{t(`${k}.title`, { n })}</>}
        description={chunk.page_start ? t(`${k}.subtitle`, { path: path || "—", page: chunk.page_start }) : path || undefined}
        closeLabel={t("common.close")}
        footer={
          <>
            <DialogClose asChild>
              <Button variant="ghost">{t("common.cancel")}</Button>
            </DialogClose>
            <Button type="submit" form="edit-chunk-form" loading={save.isPending}>
              {t(`${k}.save`)}
            </Button>
          </>
        }
      >
        <form id="edit-chunk-form" className="space-y-4" onSubmit={(event) => void form.handleSubmit((values) => { save.mutate(values.content); })(event)} noValidate>
          {chunk.context ? (
            <Field label={t(`${k}.context`)} hint={t(`${k}.contextHint`)}>
              <Textarea rows={2} className="text-xs" value={chunk.context} readOnly />
            </Field>
          ) : null}
          <Field label={t(`${k}.text`)} error={form.formState.errors.content?.message}>
            <Textarea rows={8} {...form.register("content")} />
          </Field>
          <div className="flex items-start gap-2 rounded-md bg-warning-soft px-3 py-2.5 text-xs text-fg">
            <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0 text-warning" />
            <span>{t(`${k}.warning`)}</span>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
